import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import type { HierarchyData } from '@atlassian/reporting-lines/hierarchy-chart';

import type { Collaborator, RecentWorkUrls } from '../types';

import type {
	UserProfilePreviewRelay_reportingLines$data,
	UserProfilePreviewRelay_reportingLines$key,
} from './__generated__/UserProfilePreviewRelay_reportingLines.graphql';
import type { UserProfilePreviewRelay_smarts$key } from './__generated__/UserProfilePreviewRelay_smarts.graphql';
import type { UserProfilePreviewRelay_smarts$data } from './__generated__/UserProfilePreviewRelay_smarts.graphql';
import type { UserProfilePreviewRelay_user$key } from './__generated__/UserProfilePreviewRelay_user.graphql';
import type { UserProfilePreviewRelay_workedOn$data } from './__generated__/UserProfilePreviewRelay_workedOn.graphql';
import type { UserProfilePreviewRelay_workedOn$key } from './__generated__/UserProfilePreviewRelay_workedOn.graphql';
import { UserProfilePreview, type UserProfilePreviewProps } from './UserProfilePreview';

export type UserProfilePreviewRelayProps = Omit<UserProfilePreviewProps, 'recentWorkData'> & {
	userFragment?: UserProfilePreviewRelay_user$key;
	workedOnFragment?: UserProfilePreviewRelay_workedOn$key;
	smartsFragment?: UserProfilePreviewRelay_smarts$key;
	reportingLinesFragment?: UserProfilePreviewRelay_reportingLines$key;
};

function buildAbsoluteUrl(base?: string | null, webUi?: string | null) {
	if (!webUi) {
		return undefined;
	}
	if (!base) {
		return webUi;
	}
	try {
		return new URL(webUi, base).toString();
	} catch {
		return webUi;
	}
}

function mapRelayWorkedOnConnectionToRecentWorkItems(
	data: UserProfilePreviewRelay_workedOn$data,
): RecentWorkUrls {
	const result: string[] = [];

	for (const edge of data.edges) {
		const item = edge?.node;
		const object = item?.object;
		if (!object) {
			continue;
		}

		const objectData = object.data;
		const dataTypeName = objectData?.__typename;

		let url = '';
		if (
			dataTypeName === 'ConfluencePage' ||
			dataTypeName === 'ConfluenceBlogPost' ||
			dataTypeName === 'ConfluenceWhiteboard'
		) {
			url = buildAbsoluteUrl(objectData?.links?.base, objectData?.links?.webUi) ?? '';
		} else if (dataTypeName === 'JiraIssue') {
			url = objectData?.webUrl ?? '';
		} else if (
			dataTypeName === 'JiraPlatformComment' ||
			dataTypeName === 'JiraServiceManagementComment'
		) {
			url = objectData?.webUrl ?? objectData?.issue?.webUrl ?? '';
		}

		if (!url) {
			continue;
		}
		result.push(url);
	}

	return result;
}

type UserProfilePreviewRelay_reportingLinesKd = Extract<
	UserProfilePreviewRelay_reportingLines$data['reportingLines'],
	{ __typename: 'KnowledgeDiscoveryReportingLines' }
>;

/** `user` / `managers` / `reports` rows — same shape as `UserProfilePreviewRelay_reportingLines` fragment data. */
function mapKdReportingLinesUser(
	u: UserProfilePreviewRelay_reportingLinesKd['user'],
): HierarchyData['user'] | null {
	if (!u?.accountIdentifier) {
		return null;
	}
	return {
		accountId: u.accountIdentifier,
		name: u.pii?.name ?? '',
		avatarUrl: u.pii?.picture ?? null,
		jobTitle: u.pii?.extendedProfile?.jobTitle ?? null,
		numberOfDirectReports: u.numberOfDirectReports ?? undefined,
		numberOfTotalReports: u.numberOfTotalReports ?? undefined,
	};
}

function mapKnowledgeDiscoveryReportingLinesToHierarchyData(
	data: UserProfilePreviewRelay_reportingLines$data | null | undefined,
): HierarchyData | undefined {
	if (!data?.reportingLines) {
		return undefined;
	}
	const rl = data.reportingLines;
	if (rl.__typename !== 'KnowledgeDiscoveryReportingLines') {
		return undefined;
	}
	const user = mapKdReportingLinesUser(rl.user);
	if (!user) {
		return undefined;
	}
	const managerUser =
		rl.managers?.length && rl.managers[0] ? mapKdReportingLinesUser(rl.managers[0]) : null;
	const reports =
		rl.reports
			?.map((r) => mapKdReportingLinesUser(r))
			.filter((r): r is HierarchyData['user'] => r != null) ?? [];
	return {
		manager: managerUser ?? undefined,
		user,
		reports: reports.length ? reports : undefined,
	};
}

function mapRelaySmartsToCollaborators(
	data: UserProfilePreviewRelay_smarts$data,
	accountId?: string,
): Collaborator[] {
	const recommended = data.recommendedUser ?? [];
	if (!recommended.length) {
		return [];
	}

	return recommended.reduce<Collaborator[]>((acc, node) => {
		const user = node?.user;
		if (!user) {
			return acc;
		}
		const id = user.accountId;
		if (!id || (accountId && id === accountId)) {
			return acc;
		}
		acc.push({
			accountId: id ?? null,
			name: user.name ?? null,
			avatarUrl: user.picture ?? null,
			email: user.email ?? null,
			jobTitle: user.extendedProfile?.jobTitle ?? null,
		});
		return acc;
	}, []);
}

export function UserProfilePreviewRelay({
	userFragment,
	workedOnFragment,
	smartsFragment,
	reportingLinesFragment,
	...props
}: UserProfilePreviewRelayProps): React.JSX.Element {
	const relayUserData = useFragment(
		graphql`
			fragment UserProfilePreviewRelay_user on User {
				accountId
				name
				picture
				... on AtlassianAccountUser {
					email
					extendedProfile {
						jobTitle
						department
						location
						organization
					}
				}
			}
		`,
		userFragment ?? null,
	);

	const workedOnData = useFragment(
		graphql`
			fragment UserProfilePreviewRelay_workedOn on ActivityConnection {
				edges {
					node {
						object {
							data {
								__typename
								... on ConfluencePage {
									links {
										base
										webUi
									}
								}
								... on ConfluenceBlogPost {
									links {
										base
										webUi
									}
								}
								... on ConfluenceWhiteboard {
									links {
										base
										webUi
									}
								}
								... on JiraIssue {
									webUrl
								}
								... on JiraPlatformComment {
									webUrl
									issue {
										webUrl
									}
								}
								... on JiraServiceManagementComment {
									webUrl
									issue {
										webUrl
									}
								}
							}
						}
					}
				}
			}
		`,
		workedOnFragment ?? null,
	);

	const smartsData = useFragment(
		graphql`
			fragment UserProfilePreviewRelay_smarts on SmartsQueryApi
			@argumentDefinitions(recommendationsQuery: { type: "SmartsRecommendationsQuery!" }) {
				recommendedUser(recommendationsQuery: $recommendationsQuery) {
					user {
						accountId
						name
						picture
						... on AtlassianAccountUser {
							email
							extendedProfile {
								jobTitle
							}
						}
					}
				}
			}
		`,
		smartsFragment ?? null,
	);

	const reportingLinesData = useFragment(
		graphql`
			fragment UserProfilePreviewRelay_reportingLines on KnowledgeDiscoveryQueryApi
			@argumentDefinitions(
				aaid: { type: "String!" }
				orgId: { type: "String!" }
				siteId: { type: "String!" }
			) {
				reportingLines(aaid: $aaid, orgId: $orgId, siteId: $siteId)
					@optIn(to: "KnowledgeDiscovery Get reporting lines") {
					__typename
					... on KnowledgeDiscoveryReportingLines {
						user {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						managers {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						reports {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
					}
				}
			}
		`,
		reportingLinesFragment ?? null,
	);

	const relayHierarchyData = useMemo(
		() => mapKnowledgeDiscoveryReportingLinesToHierarchyData(reportingLinesData),
		[reportingLinesData],
	);

	const recentWorkData = useMemo(() => {
		if (!workedOnData) {
			return undefined;
		}
		const mapped = mapRelayWorkedOnConnectionToRecentWorkItems(workedOnData);
		return mapped.length ? mapped : undefined;
	}, [workedOnData]);

	const relayProfileData = useMemo(() => {
		if (!relayUserData?.accountId) {
			return undefined;
		}
		return {
			accountId: relayUserData.accountId,
			name: relayUserData.name ?? '',
			avatarUrl: relayUserData.picture ?? null,
			email: relayUserData.email ?? null,
			jobTitle: relayUserData.extendedProfile?.jobTitle ?? null,
			department: relayUserData.extendedProfile?.department ?? null,
			location: relayUserData.extendedProfile?.location ?? null,
			organization: relayUserData.extendedProfile?.organization ?? null,
		};
	}, [relayUserData]);

	const relayCollaborators = useMemo(() => {
		if (!smartsData) {
			return undefined;
		}
		const mapped = mapRelaySmartsToCollaborators(smartsData, relayProfileData?.accountId);
		return mapped.length ? mapped : undefined;
	}, [smartsData, relayProfileData?.accountId]);

	return (
		<UserProfilePreview
			{...props}
			data={relayProfileData ?? props.data}
			hierarchyData={relayHierarchyData ?? props.hierarchyData}
			recentWorkData={recentWorkData}
			collaborators={relayCollaborators ?? props.collaborators}
		/>
	);
}

export type {
	UserProfilePreviewRelay_reportingLines$key,
	UserProfilePreviewRelay_smarts$key,
	UserProfilePreviewRelay_workedOn$key,
};
